/**
 * W3C trace-context helpers with zero dependencies.
 *
 * Deliberately not built on `@opentelemetry/api`, because two of the four
 * runtimes that need this (the MV3 service worker, and the edge proxy) either
 * cannot load the SDK or should not pay for it.
 *
 * Format: `00-<32 hex trace id>-<16 hex span id>-<2 hex flags>`, flags 01 = sampled.
 */

const TRACEPARENT_RE = /^00-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/;
const ZERO_TRACE = '0'.repeat(32);
const ZERO_SPAN = '0'.repeat(16);

export interface TraceContext {
  traceId: string;
  spanId: string;
  sampled: boolean;
}

function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  // Present in browsers, Node >=19, edge runtimes and MV3 service workers.
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoObj?.getRandomValues) {
    cryptoObj.getRandomValues(buf);
  } else {
    for (let i = 0; i < bytes; i += 1) buf[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function parseTraceparent(header: string | null | undefined): TraceContext | undefined {
  if (!header) return undefined;
  const match = TRACEPARENT_RE.exec(header.trim());
  if (!match) return undefined;

  const [, traceId, spanId, flags] = match;
  // All-zero ids are explicitly invalid per the spec.
  if (traceId === ZERO_TRACE || spanId === ZERO_SPAN) return undefined;

  return { traceId, spanId, sampled: (parseInt(flags, 16) & 1) === 1 };
}

export function formatTraceparent(context: TraceContext): string {
  return `00-${context.traceId}-${context.spanId}-${context.sampled ? '01' : '00'}`;
}

/** Start a brand new trace. Used where there is no inbound context, e.g. the extension. */
export function generateTraceparent(sampled = true): string {
  return formatTraceparent({ traceId: randomHex(16), spanId: randomHex(8), sampled });
}

/** Continue an existing trace under a fresh child span id. */
export function childTraceparent(parent: string | TraceContext | undefined): string {
  const context = typeof parent === 'string' ? parseTraceparent(parent) : parent;
  if (!context) return generateTraceparent();
  return formatTraceparent({ ...context, spanId: randomHex(8) });
}

export const newSpanId = (): string => randomHex(8);

/**
 * Coerce an arbitrary id into a valid W3C id of `length` hex chars.
 *
 * Mastra documents its trace ids as "1-32 hexadecimal characters", so a short
 * or non-hex id would be rejected by the OTLP transformer. Hashing is stable,
 * so the same input always maps to the same output and traces still join.
 */
export function coerceHexId(value: string, length: 32 | 16): string {
  const cleaned = value.toLowerCase().replace(/[^0-9a-f]/g, '');
  if (cleaned.length === length) return cleaned;
  if (cleaned.length > length) return cleaned.slice(0, length);

  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < value.length; i += 1) {
    const c = value.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  let out = cleaned;
  let salt = 0;
  while (out.length < length) {
    const mixed = Math.imul(h1 ^ (h2 + salt), 2246822507) >>> 0;
    out += mixed.toString(16).padStart(8, '0');
    salt += 1;
  }
  return out.slice(0, length);
}
